import { useEffect, useState } from 'react';
import { Button, DatePicker, Empty, Row, Select, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Navigate, useSearchParams } from 'react-router-dom';
import { AiOutlineStop } from 'react-icons/ai';
import dayjs from 'dayjs';
import { useTheme } from 'styled-components';
import { PageWrapper } from 'components/PageWrapper';
import { PageHeader } from 'components/PageHeader';
import { Heading1 } from 'components/Heading1';
import { Table } from 'components/Table';
import { TextM, TextS } from 'components/Text';
import { useBreadcrumb } from 'modules/core/context/AppContext';
import { BreadcrumbContainer } from 'components/Breadcrumb';
import { UsersIcon } from 'modules/users/constants/routes';
import { useCurrentProfile } from 'modules/core/context/ProfileContext';
import { AccessListContent, AccessPermission, AccessType } from 'types/Access';
import { AccessService } from 'modules/users/services/access';
import { PaginationInfo } from 'types/List';
import { PermissionAuditContent } from 'types/PermissionAudit';
import { AccessPermissionDescription } from 'constants/access';
import { formatCurrency, normalizeCpf } from 'helpers/normalizers';

const PermissionAudits = () => {
	const theme = useTheme();
	const { setBreadcrumb } = useBreadcrumb();
	const { person, type, permissions } = useCurrentProfile();

	const [paginationInfo, setPaginationInfo] = useState<PaginationInfo>({
		currentPage: 1,
		pageSize: 10,
	});

	const [searchParams] = useSearchParams();
	const [userId, setUserId] = useState<string | undefined>(
		searchParams.get('user_id') ?? undefined,
	);
	const [dateRange, setDateRange] = useState<
		[dayjs.Dayjs | null, dayjs.Dayjs | null] | null
	>(null);

	const personId = person?.id;

	const dateFrom = dateRange?.[0]?.format('YYYY-MM-DD');
	const dateTo = dateRange?.[1]?.format('YYYY-MM-DD');

	const { data: accessListData } = useQuery({
		queryKey: ['permissionAuditsUsers', personId],
		queryFn: () =>
			AccessService.getAccessList({
				...(personId ? { person_id: personId } : {}),
				size: 1000,
			}),
		staleTime: 60000,
	});

	const userOptions = Array.from(
		new Map(
			(accessListData?.content ?? []).map((access: AccessListContent) => [
				access.user.id,
				{ label: access.user.full_name, value: access.user.id },
			]),
		).values(),
	);

	const hasActiveFilters = Boolean(userId || dateRange);

	const { data, isLoading, refetch } = useQuery({
		queryKey: [
			'permissionAudits',
			personId,
			userId,
			dateFrom,
			dateTo,
			paginationInfo,
		],
		queryFn: () =>
			AccessService.getPermissionAudits({
				...(personId ? { person_id: personId } : {}),
				...(userId ? { user_id: userId } : {}),
				...(dateFrom ? { date_from: dateFrom } : {}),
				...(dateTo ? { date_to: dateTo } : {}),
				page: paginationInfo.currentPage,
				size: paginationInfo.pageSize,
			}),
		placeholderData: keepPreviousData,
	});

	useEffect(() => {
		setBreadcrumb([
			{
				href: '/users',
				title: (
					<BreadcrumbContainer>
						<UsersIcon />
						<span>Acessos</span>
					</BreadcrumbContainer>
				),
			},
			{
				title: (
					<BreadcrumbContainer>
						<span>Logs de permissão</span>
					</BreadcrumbContainer>
				),
			},
		]);
	}, [setBreadcrumb]);

	useEffect(() => {
		refetch();
	}, [paginationInfo, userId, dateFrom, dateTo, refetch]);

	const columns: ColumnsType<PermissionAuditContent> = [
		{
			title: 'Data/hora',
			width: '18%',
			render: (row: PermissionAuditContent) => (
				<div>
					<TextM weight="bold">
						{dayjs(row.created_at).format('DD/MM/YYYY')}
					</TextM>
					<TextS weight="normal" color={theme.textSecondary}>
						às {dayjs(row.created_at).format('HH:mm')}
					</TextS>
				</div>
			),
		},
		{
			title: 'Usuário editado',
			width: '22%',
			render: (row: PermissionAuditContent) =>
				row.edited_user ? (
					<div>
						<TextM weight="bold">{row.edited_user.full_name}</TextM>
						<TextS weight="normal" color={theme.textSecondary}>
							{normalizeCpf(row.edited_user.taxpayer_id)}
						</TextS>
					</div>
				) : (
					<TextM weight="bold">-</TextM>
				),
		},
		{
			title: 'Alteração',
			width: '40%',
			render: (row: PermissionAuditContent) => {
				const limitActive =
					row.posting_approval_limit_active === true &&
					row.posting_approval_limit != null;
				return (
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							gap: 6,
						}}
					>
						{(row.permissions_added.length > 0 ||
							row.permissions_removed.length > 0) && (
							<div
								style={{
									display: 'flex',
									flexWrap: 'wrap',
									gap: 4,
								}}
							>
								{row.permissions_added.map(permission => (
									<Tag
										key={`add-${permission}`}
										color="green"
									>
										+{' '}
										{
											AccessPermissionDescription[
												permission
											]
										}
									</Tag>
								))}
								{row.permissions_removed.map(permission => (
									<Tag key={`rem-${permission}`} color="red">
										−{' '}
										{
											AccessPermissionDescription[
												permission
											]
										}
									</Tag>
								))}
							</div>
						)}
						<TextS weight="normal" color={theme.textSecondary}>
							{limitActive
								? `Limite de aprovação: ${formatCurrency(
										row.posting_approval_limit as number,
									)}`
								: 'Limite de aprovação: desativado'}
						</TextS>
					</div>
				);
			},
		},
		{
			title: 'Responsável',
			width: '20%',
			render: (row: PermissionAuditContent) => (
				<TextM weight="normal">{row.operator.full_name}</TextM>
			),
		},
	];

	if (
		type !== AccessType.FINANCIAL_INSTITUTION &&
		!permissions.includes(AccessPermission.MANAGE_USER)
	) {
		return <Navigate to="/" />;
	}

	return (
		<>
			<PageHeader>
				<Heading1>Logs de permissão</Heading1>
			</PageHeader>
			<PageWrapper>
				<Row
					align="middle"
					style={{ marginBottom: '1rem', gap: '1rem' }}
				>
					<Select
						allowClear
						showSearch
						size="large"
						optionFilterProp="label"
						placeholder="Filtrar por usuário"
						style={{ minWidth: 240 }}
						value={userId}
						options={userOptions}
						onChange={value => {
							setUserId(value);
							setPaginationInfo({ currentPage: 1, pageSize: 10 });
						}}
					/>
					<DatePicker.RangePicker
						size="large"
						format="DD/MM/YYYY"
						value={dateRange}
						onChange={values => {
							setDateRange(values);
							setPaginationInfo({ currentPage: 1, pageSize: 10 });
						}}
					/>
					<Button
						size="large"
						icon={<AiOutlineStop size={16} />}
						disabled={!hasActiveFilters}
						onClick={() => {
							setUserId(undefined);
							setDateRange(null);
							setPaginationInfo({ currentPage: 1, pageSize: 10 });
						}}
					>
						Limpar filtros
					</Button>
				</Row>
				<Table
					columns={columns}
					rowKey={record => record.id}
					dataSource={data?.content || []}
					loading={isLoading}
					locale={{
						emptyText: (
							<Empty description="Nenhum registro encontrado" />
						),
					}}
					pagination={{
						total: data?.total_elements || 0,
						showTotal(total) {
							const currentSize =
								paginationInfo.currentPage *
								paginationInfo.pageSize;
							return `${currentSize > total ? total : currentSize} de ${total}`;
						},
						pageSizeOptions: ['10', '30', '60', '90'],
						showSizeChanger: true,
						pageSize: paginationInfo.pageSize,
						current: paginationInfo.currentPage,
						onChange(page, pageSize) {
							setPaginationInfo({
								currentPage: page,
								pageSize,
							});
						},
					}}
				/>
			</PageWrapper>
		</>
	);
};

export default PermissionAudits;
